import { Trans } from "@lingui/react/macro";
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import dayjs from "dayjs";
import type { TimeOffOverlapConflictBody } from "@/server-types";
import { resolveTimeOffTypeName } from "../TimeOffTypes/timeOffTypeDisplayName";

type TimeOffOverlap = TimeOffOverlapConflictBody["overlaps"][number];

function formatSpan(overlap: TimeOffOverlap): string {
  const from = dayjs(overlap.from);
  const to = dayjs(overlap.to);

  return from.isSame(to, "day")
    ? `${from.format("D. M. YYYY HH:mm")}–${to.format("HH:mm")}`
    : `${from.format("D. M. YYYY HH:mm")} – ${to.format("D. M. YYYY HH:mm")}`;
}

function groupByPerson(overlaps: TimeOffOverlap[]) {
  const groups = new Map<
    number,
    { userId: number; userName: string; rows: TimeOffOverlap[] }
  >();

  for (const overlap of overlaps) {
    const group = groups.get(overlap.userId);
    if (group) group.rows.push(overlap);
    else
      groups.set(overlap.userId, {
        userId: overlap.userId,
        userName: overlap.userName,
        rows: [overlap],
      });
  }

  return [...groups.values()];
}

/** Replace deletes each listed row whole, so every row shows its full span. */
export function TimeOffOverlapDialog({
  conflict,
  isSaving,
  onDismiss,
  onChoose,
}: {
  conflict: TimeOffOverlapConflictBody | null;
  isSaving: boolean;
  onDismiss: () => void;
  onChoose: (onOverlap: "SKIP" | "REPLACE") => void;
}) {
  return (
    <Modal
      isOpen={conflict != null}
      onOpenChange={onDismiss}
      isDismissable={!isSaving}
      isKeyboardDismissDisabled={isSaving}
      hideCloseButton={isSaving}
      size="md"
    >
      <ModalContent>
        <ModalHeader>
          <Trans>Odsotnost se prekriva</Trans>
        </ModalHeader>
        <ModalBody>
          <p className="text-gray-500 text-sm">
            <Trans>Izbrani zaposleni so v tem obdobju že odsotni:</Trans>
          </p>
          <div
            className="flex flex-col gap-3"
            data-identifier="time-off-overlap-list"
          >
            {groupByPerson(conflict?.overlaps ?? []).map((group) => (
              <div key={group.userId} className="flex flex-col gap-1">
                <span className="font-medium text-sm">{group.userName}</span>
                {group.rows.map((row) => (
                  <span
                    key={row.timeOffId}
                    className="text-default-500 text-sm"
                  >
                    {row.typeEmoji ? `${row.typeEmoji} ` : ""}
                    {resolveTimeOffTypeName({
                      name: row.typeName,
                      systemKey: row.typeSystemKey,
                    })}
                    {" · "}
                    {formatSpan(row)}
                  </span>
                ))}
              </div>
            ))}
          </div>
        </ModalBody>
        <ModalFooter className="flex-col gap-2">
          <Button
            fullWidth
            color="primary"
            type="button"
            isLoading={isSaving}
            data-identifier="time-off-overlap-replace"
            onPress={() => onChoose("REPLACE")}
          >
            <Trans>Zamenjaj obstoječe odsotnosti</Trans>
          </Button>
          {conflict?.hasFreeShifts && (
            <Button
              fullWidth
              variant="bordered"
              type="button"
              isDisabled={isSaving}
              data-identifier="time-off-overlap-skip"
              onPress={() => onChoose("SKIP")}
            >
              <Trans>Dodaj samo na proste dni</Trans>
            </Button>
          )}
          <Button
            fullWidth
            variant="light"
            type="button"
            isDisabled={isSaving}
            onPress={onDismiss}
          >
            <Trans>Prekliči</Trans>
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
